import MoreTimeIcon from "@mui/icons-material/MoreTime";
import {Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {TemporalFormat, useFlag} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {useAsyncCallback} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {useBooking} from "../../context/booking";
import {B2Booking} from "../../data/client.gen";
import {getZoneDateTimeNow, parseZoneDateTimeFromInstant} from "../../dates-utils";
import {useLocalization} from "../../i18n";
import {isBlank, notBlank} from "../../utils";

/**
 * Whether the paid prolongation offer is currently open: {@code prolongableFrom} is set and has passed. The
 * timestamp comes from the backend (booking end minus the site's ending-soon lead time — the admin setting reads
 * "allow customers to prolong their booking N h before booking end").
 */
function isProlongationOffered(prolongableFrom: string | undefined) {
	return notBlank(prolongableFrom) && Date.parse(prolongableFrom as string) <= Date.now();
}

/**
 * Live view of {@link isProlongationOffered}. While the offer has not opened yet it schedules a single wake-up at the
 * moment it opens — instead of polling — and refetches the booking then. The reveal is deliberately not a pure
 * function of time: eligibility also needs an active occupancy, which can end server-side while the customer leaves
 * the view open. Refetching (rather than blindly flipping the flag) lets the backend re-decide, so a booking that is
 * no longer prolongable clears {@code prolongableFrom} and this effect hides the nudge again instead of routing the
 * customer to a {@code /prolong} that then fails.
 *
 * Call this once per view and pass the result down; the shared {@code refresh} identity is stable, so it does not
 * re-arm on every unrelated booking refresh.
 */
export function useProlongationOffered(booking: B2Booking | undefined, refresh: () => Promise<unknown>): boolean {
	const prolongableFrom = booking?.prolongableFrom;
	const [offered, setOffered] = useState(() => isProlongationOffered(prolongableFrom));
	useEffect(() => {
		if (isProlongationOffered(prolongableFrom)) {
			setOffered(true);
			return;
		}
		setOffered(false);
		if (isBlank(prolongableFrom)) {
			return;
		}
		const delay = Math.max(0, Date.parse(prolongableFrom as string) - Date.now());
		const timeout = setTimeout(() => {
			setOffered(true);
			void refresh();
		}, delay);
		return () => clearTimeout(timeout);
	}, [prolongableFrom, refresh]);
	return offered;
}

export function BookingExpirationCountDown({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {cancel: cancelBooking} = useBooking();

	const [expiration, setExpiration] = useState("");
	const [cancelPrompt, setCancelPrompt, clearCancelPrompt] = useFlag(false);

	function computeTimeRemain(booking: B2Booking) {
		const expiredAfter = booking.site.cancelUnpaidBookingAfter ?? 60;
		const zoneId = booking.site.zoneId ?? "UTC";
		const duration = getZoneDateTimeNow(zoneId)
			.until(
				parseZoneDateTimeFromInstant(booking.createdAt, zoneId)
					.add({minutes: expiredAfter}),
				{largestUnit: "minutes"},
			);
		setExpiration(`${duration.minutes}:${duration.seconds}`);
		return duration;
	}

	useEffect(() => {
		computeTimeRemain(booking);
		const interval = setInterval(() => {
			const duration = computeTimeRemain(booking);
			if (duration.minutes <= 0 && duration.seconds <= 0) {
				clearInterval(interval);
			}
		}, 1000);
		return () => {
			clearInterval(interval);
		};
	}, [booking]);

	const {loading, error, execute} = useAsyncCallback(cancelBooking);

	async function handleCancelBooking() {
		await execute();
		clearCancelPrompt();
	}

	return (
		<>
			<Alert
				severity="warning"
				action={
					<Button
						sx={{minWidth: 120}}
						size="small"
						variant="contained"
						color="secondary"
						disableElevation
						onClick={setCancelPrompt}
					>
						{t("booking.cancel.button")}
					</Button>
				}
			>
				<Typography variant="body2">{t("payment.reminder", {expiration})}</Typography>
			</Alert>
			<Alert
				severity="warning"
				action={
					<Button
						sx={{
							minWidth: 120,
						}}
						size="small"
						variant="contained"
						color="primary"
						disableElevation
						onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/payment`)}
					>
						{t("actions.pay")}
					</Button>
				}
			>
				<Typography variant="body2">{t("payment.completePaymentPrompt")}</Typography>
			</Alert>
			<Dialog open={cancelPrompt}>
				<DialogTitle>{t("booking.cancel.title")}</DialogTitle>
				<DialogContent>
					{t("booking.cancel.message")}
					{error && <Alert severity="error">{"Failed to cancel booking."}</Alert>}
				</DialogContent>
				<DialogActions>
					<Button
						size="small"
						variant="contained"
						color="primary"
						disabled={loading}
						onClick={handleCancelBooking}
					>
						{t("common.yes")}
					</Button>
					<Button size="small" color="primary" disabled={loading} onClick={clearCancelPrompt}>
						{t("common.no")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export function BookingCancelHint() {
	const {t} = useLocalization();
	return (
		<Alert severity="warning">
			<Typography variant="body2">{t("booking.canceled")}</Typography>
		</Alert>
	);
}

/**
 * Shown in place of the locker card while the booking has not started yet.
 */
export function BookingNotStartedAlert({booking}: { booking: B2Booking }) {
	const {t} = useLocalization();
	const zoneId = booking.site.zoneId ?? "UTC";
	return (
		<Alert severity="info">
			<Typography variant="body2">
				{t("booking.notStarted")} <TemporalFormat value={parseZoneDateTimeFromInstant(booking.from, zoneId)} />
			</Typography>
		</Alert>
	);
}

/**
 * Shown in place of the locker card once the booking timeframe (including any access grace period) is over.
 * Merges the previous "booking has ended" info and the prolongation prompt into one alert (#1179) that offers
 * the customer the way forward:
 * - lockers still occupied and the booking is prolongable → prolong the booking,
 * - lockers still occupied but set to auto-release → keep them (the same prevent-auto-end page the
 *   "ending soon" email links to),
 * - no lockers left → book another one at the site.
 */
interface BookingEndedAlertProps {
	booking: B2Booking;
	prolongationOffered: boolean;
}

export function BookingEndedAlert({booking, prolongationOffered}: BookingEndedAlertProps) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	// booking.occupancies already excludes ended/released occupancies (the backend splits them into
	// endedOccupancies), so a non-empty list means lockers are still actively held.
	const hasActiveOccupancies = (booking.occupancies ?? []).length > 0;

	let message = t("booking.ended");
	let action: React.ReactNode;
	if (!hasActiveOccupancies) {
		message = t("booking.endedAlert.releasedMessage");
		action = (
			<EndedAlertAction onClick={() => navigate(`/${booking.tenant.id}/${booking.site.id}`)}>
				{t("booking.endedAlert.bookAnotherButton")}
			</EndedAlertAction>
		);
	}
	else if (prolongationOffered) {
		message = t("booking.endedAlert.prolongMessage");
		action = (
			<EndedAlertAction onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/prolong`)}>
				{t("booking.prolong.button")}
			</EndedAlertAction>
		);
	}
	else if (notBlank(booking.keepLockersUrl)) {
		message = t("booking.endedAlert.keepMessage");
		action = (
			<EndedAlertAction href={booking.keepLockersUrl}>
				{t("booking.endedAlert.keepButton")}
			</EndedAlertAction>
		);
	}

	return (
		<Alert severity="error" action={action}>
			<AlertTitle>{t("booking.endedAlert.title")}</AlertTitle>
			<Typography variant="body2">{message}</Typography>
		</Alert>
	);
}

interface EndedAlertActionProps {
	onClick?: () => void;
	href?: string;
	children: React.ReactNode;
}

function EndedAlertAction(props: EndedAlertActionProps) {
	return <Button size="small" color="inherit" variant="outlined" sx={{whiteSpace: "nowrap"}} {...props} />;
}

/** Informational nudge to prolong a running booking, shown at the end of the locker card (#1179). */
export function BookingProlongationButton({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	return (
		<Alert
			severity="info"
			icon={<MoreTimeIcon fontSize="inherit" />}
			action={
				<Button
					size="small"
					variant="contained"
					color="primary"
					disableElevation
					onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/prolong`)}
				>
					{t("booking.prolong.button")}
				</Button>
			}
		>
			<AlertTitle>{t("booking.prolong.title")}</AlertTitle>
			<Typography variant="body2">{t("booking.prolong.message")}</Typography>
		</Alert>
	);
}

export function PendingPaymentButton({booking}: { booking: B2Booking }) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	return (
		<Alert
			severity="warning"
			action={
				<Button
					size="small"
					variant="contained"
					color="primary"
					disableElevation
					onClick={() => navigate(`/${booking.tenant.id}/bookings/${booking.id}/payment`)}
				>
					{t("actions.pay")}
				</Button>
			}
		>
			<Typography variant="body2">{t("payment.completePaymentPrompt")}</Typography>
		</Alert>
	);
}
